/**
 * ManageSharingModal Component
 * Unified modal for managing item sharing - both groups and email shares
 * Replaces separate ShareModal and ItemShareModal
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Mail, Copy, Check, Trash2, Clock, Eye, Edit, ExternalLink, Users } from 'lucide-react';
import ModalCloseButton from './ModalCloseButton';
import { useAuth } from '../contexts/AuthContext';
import {
  SharingGroup,
  CreateGroupData,
  ItemShare,
  ShareItemResponse,
  getUserGroups,
  createGroup,
  shareItemsToGroup,
  setDefaultGroup,
  getDefaultGroup,
  deleteGroup,
  updateGroup,
  getItemsSharedGroups,
  unshareItem,
  shareItemByEmail,
  getItemShares,
  revokeItemShare
} from '../services/sharingService';
import GroupMembersModal from './GroupMembersModal';
import '../styles/components/manageSharingModal.css';

interface ManageSharingModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedItems: number[];  // Item IDs to share/manage
  itemName?: string;  // Display name for single item, or "X items" for bulk
  onSuccess?: () => void;
}

type TabType = 'groups' | 'email';

const ManageSharingModal: React.FC<ManageSharingModalProps> = ({
  isOpen,
  onClose,
  selectedItems,
  itemName,
  onSuccess
}) => {
  const { token } = useAuth();
  const isBulkMode = selectedItems.length > 1;
  const singleItemId = selectedItems.length === 1 ? selectedItems[0] : null;
  
  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('groups');
  
  // Shared state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Groups tab state
  const [groups, setGroups] = useState<SharingGroup[]>([]);
  const [defaultGroupId, setDefaultGroupId] = useState<number | null>(null);
  const [sharedGroups, setSharedGroups] = useState<Record<number, Array<{ group_id: number; group_name: string; group_type: string }>>>({});
  const [selectedGroupForShare, setSelectedGroupForShare] = useState<SharingGroup | null>(null);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<SharingGroup | null>(null);
  const [groupsView, setGroupsView] = useState<'select' | 'create'>('select');
  
  // Create group form
  const [groupName, setGroupName] = useState('');
  const [groupType, setGroupType] = useState<'family' | 'team' | 'temporary'>('family');
  const [groupDescription, setGroupDescription] = useState('');
  const [setAsDefault, setSetAsDefault] = useState(false);
  
  // Edit group state
  const [editingGroupId, setEditingGroupId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSetAsDefault, setEditSetAsDefault] = useState(false);
  
  // Email tab state
  const [emailView, setEmailView] = useState<'share' | 'manage'>('share');
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState<'view' | 'edit'>('view');
  const [message, setMessage] = useState('');
  const [expiresDays, setExpiresDays] = useState<number | null>(null);
  const [shareResult, setShareResult] = useState<ShareItemResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [existingShares, setExistingShares] = useState<ItemShare[]>([]);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && token && selectedItems.length > 0) {
      loadGroups();
      loadDefaultGroup();
      loadSharedGroups();
      if (singleItemId) {
        loadExistingEmailShares();
      }
    }
  }, [isOpen, token, selectedItems.length, singleItemId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('groups');
      setError(null);
      setSuccess(null);
      setGroupsView('select');
      setSelectedGroupForShare(null);
      setEditingGroupId(null);
      setEmailView('share');
      setEmail('');
      setPermission('view');
      setMessage('');
      setExpiresDays(null);
      setShareResult(null);
      setCopied(false);
      setGroupName('');
      setGroupDescription('');
      setSetAsDefault(false);
      setSharedGroups({});  // Reset shared groups to avoid stale data
      setExistingShares([]);  // Reset email shares too
    }
  }, [isOpen]);

  // Keyboard handling
  useEffect(() => {
    if (!isOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (groupsView === 'create') {
          setGroupsView('select');
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose, groupsView]);

  // ============ Groups Tab Functions ============
  
  const loadGroups = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const userGroups = await getUserGroups(token);
      setGroups(userGroups);
    } catch (err: any) {
      setError(err?.message || 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const loadDefaultGroup = async () => {
    if (!token) return;
    try {
      const result = await getDefaultGroup(token);
      setDefaultGroupId(result.default_group_id);
    } catch (err) {
      console.error('Failed to load default group:', err);
    }
  };

  const loadSharedGroups = async () => {
    if (!token || selectedItems.length === 0) return;
    try {
      const result = await getItemsSharedGroups(token, selectedItems);
      setSharedGroups(result);
    } catch (err) {
      console.error('Failed to load shared groups:', err);
    }
  };

  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !groupName.trim()) {
      setError('Group name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data: CreateGroupData = {
        name: groupName.trim(),
        type: groupType,
        description: groupDescription.trim() || undefined
      };

      const newGroup = await createGroup(token, data);

      if (setAsDefault) {
        await setDefaultGroup(token, newGroup.id);
        setDefaultGroupId(newGroup.id);
      }

      setGroupName('');
      setGroupDescription('');
      setSetAsDefault(false);
      
      await loadGroups();
      setSelectedGroupForShare(newGroup);
      setGroupsView('select');
      setSuccess('Group created successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  const handleShareToGroup = async (groupId: number) => {
    if (!token || selectedItems.length === 0) {
      setError('No items selected');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await shareItemsToGroup(token, groupId, { item_ids: selectedItems });
      
      setSuccess(`Shared ${selectedItems.length} item${selectedItems.length > 1 ? 's' : ''} with group`);
      await loadSharedGroups();

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to share items');
    } finally {
      setLoading(false);
    }
  };

  const handleUnshareFromGroup = async (groupId: number) => {
    if (!token || selectedItems.length === 0) return;

    try {
      setLoading(true);
      setError(null);

      for (const itemId of selectedItems) {
        await unshareItem(token, groupId, itemId);
      }

      await loadSharedGroups();
      setSuccess('Items unshared from group');

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to unshare items');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async (groupId: number) => {
    if (!token) return;
    
    if (!window.confirm('Are you sure you want to delete this group? This will unshare all items from this group.')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await deleteGroup(token, groupId);
      
      if (selectedGroupForShare?.id === groupId) {
        setSelectedGroupForShare(null);
      }
      
      await loadGroups();
      setSuccess('Group deleted');
    } catch (err: any) {
      setError(err.message || 'Failed to delete group');
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = (group: SharingGroup) => {
    setEditingGroupId(group.id);
    setEditName(group.name);
    setEditDescription(group.description || '');
    setEditSetAsDefault(defaultGroupId === group.id);
  };

  const handleCancelEdit = () => {
    setEditingGroupId(null);
    setEditName('');
    setEditDescription('');
    setEditSetAsDefault(false);
  };

  const handleSaveEdit = async (groupId: number) => {
    if (!token || !editName.trim()) {
      setError('Group name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await updateGroup(token, groupId, {
        name: editName.trim(),
        description: editDescription.trim() || undefined
      });
      
      if (editSetAsDefault && defaultGroupId !== groupId) {
        await setDefaultGroup(token, groupId);
        setDefaultGroupId(groupId);
      } else if (!editSetAsDefault && defaultGroupId === groupId) {
        await setDefaultGroup(token, null);
        setDefaultGroupId(null);
      }
      
      setEditingGroupId(null);
      await loadGroups();
      setSuccess('Group updated');
    } catch (err: any) {
      setError(err.message || 'Failed to update group');
    } finally {
      setLoading(false);
    }
  };

  // ============ Email Tab Functions ============
  
  const loadExistingEmailShares = async () => {
    if (!token || !singleItemId) return;
    try {
      const shares = await getItemShares(token, singleItemId);
      setExistingShares(shares);
    } catch (err) {
      console.error('Failed to load existing shares:', err);
    }
  };

  const handleEmailShare = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token || !singleItemId || !email.trim()) {
      setError('Email is required');
      return;
    }

    // Parse comma-separated emails
    const emails = email.split(',').map(e => e.trim()).filter(e => e.length > 0);
    
    if (emails.length === 0) {
      setError('Please enter at least one email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter(e => !emailRegex.test(e));
    if (invalidEmails.length > 0) {
      setError(`Invalid email${invalidEmails.length > 1 ? 's' : ''}: ${invalidEmails.join(', ')}`);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Share with all emails
      const results: ShareItemResponse[] = [];
      const errors: string[] = [];
      
      for (const emailAddr of emails) {
        try {
          const result = await shareItemByEmail(token, singleItemId, {
            email: emailAddr,
            permission,
            message: message.trim() || undefined,
            expires_days: expiresDays || undefined
          });
          results.push(result);
        } catch (err: any) {
          errors.push(`${emailAddr}: ${err.message || 'Failed'}`);
        }
      }

      // If single email, show result as before
      if (emails.length === 1 && results.length === 1) {
        setShareResult(results[0]);
        let successMsg = results[0].recipient_is_user
          ? `Shared with ${results[0].recipient_name || results[0].recipient_email}!`
          : `Share link created for ${results[0].recipient_email}`;
        
        if (results[0].email_sent) {
          successMsg += ' Email notification sent.';
        } else {
          successMsg += ' (Email could not be sent - share the link manually)';
        }
        setSuccess(successMsg);
      } else {
        // Multiple emails - show summary
        if (results.length > 0) {
          setSuccess(`Shared with ${results.length} recipient${results.length > 1 ? 's' : ''}!`);
        }
        if (errors.length > 0) {
          setError(`Failed for: ${errors.join('; ')}`);
        }
        // Clear form for multiple shares
        setEmail('');
      }
      
      await loadExistingEmailShares();
      
      if (onSuccess && results.length > 0) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to share item');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareResult?.share_url) return;
    
    try {
      await navigator.clipboard.writeText(shareResult.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleRevokeShare = async (shareId: number) => {
    if (!token) return;
    
    if (!window.confirm('Are you sure you want to revoke this share?')) {
      return;
    }

    try {
      setLoading(true);
      await revokeItemShare(token, shareId);
      await loadExistingEmailShares();
      setSuccess('Share revoked');
    } catch (err: any) {
      setError(err.message || 'Failed to revoke share');
    } finally {
      setLoading(false);
    }
  };

  const handleNewEmailShare = () => {
    setShareResult(null);
    setEmail('');
    setMessage('');
    setSuccess(null);
  };

  // ============ Render ============
  
  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;

  const displayName = itemName || (selectedItems.length === 1 ? 'Item' : `${selectedItems.length} items`);

  const modal = (
    <div
      className="manage-sharing-modal-overlay"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="manage-sharing-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="manage-sharing-header">
          <ModalCloseButton onClick={onClose} />
          <h2>Manage Sharing</h2>
          <span className="item-count">{displayName}</span>
        </div>

        {/* Tabs */}
        <div className="manage-sharing-tabs">
          <button
            className={`tab ${activeTab === 'groups' ? 'active' : ''}`}
            onClick={() => setActiveTab('groups')}
          >
            <Users size={16} />
            Groups
          </button>
          <button
            className={`tab ${activeTab === 'email' ? 'active' : ''} ${isBulkMode ? 'disabled' : ''}`}
            onClick={() => !isBulkMode && setActiveTab('email')}
            disabled={isBulkMode}
            title={isBulkMode ? 'Email sharing is only available for single items' : undefined}
          >
            <Mail size={16} />
            Email
            {isBulkMode && <span className="disabled-hint">(single item only)</span>}
          </button>
        </div>

        {/* Messages */}
        <div className="manage-sharing-messages">
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
        </div>

        {/* Body */}
        <div className="manage-sharing-body">
          {activeTab === 'groups' && (
            <div className="groups-tab-content">
              {groupsView === 'select' ? (
                <>
                  {/* Create new group button */}
                  <button
                    className="create-group-button"
                    onClick={() => setGroupsView('create')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    Create New Group
                  </button>

                  {loading ? (
                    <div className="loading">Loading groups...</div>
                  ) : groups.length === 0 ? (
                    <div className="empty-state">
                      <p>You don't have any groups yet.</p>
                      <p>Create your first group to start sharing!</p>
                    </div>
                  ) : (
                    <div className="groups-list">
                      {groups.map((group) => {
                        const itemsSharedWithGroup = selectedItems.filter(
                          itemId => sharedGroups[itemId]?.some(g => g.group_id === group.id)
                        ).length;
                        const isSharedWithGroup = itemsSharedWithGroup > 0;
                        
                        return (
                          <div 
                            key={group.id} 
                            className={`group-item ${selectedGroupForShare?.id === group.id ? 'selected' : ''} ${editingGroupId === group.id ? 'editing' : ''}`}
                            onClick={() => editingGroupId !== group.id && setSelectedGroupForShare(group)}
                          >
                            {editingGroupId === group.id ? (
                              <div className="edit-group-form">
                                <input
                                  type="text"
                                  value={editName}
                                  onChange={(e) => setEditName(e.target.value)}
                                  placeholder="Group name"
                                  className="edit-name-input"
                                  autoFocus
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <textarea
                                  value={editDescription}
                                  onChange={(e) => setEditDescription(e.target.value)}
                                  placeholder="Description (optional)"
                                  className="edit-description-input"
                                  rows={2}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <label className="edit-checkbox-label" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    checked={editSetAsDefault}
                                    onChange={(e) => setEditSetAsDefault(e.target.checked)}
                                  />
                                  <span>Add new items to this group</span>
                                </label>
                                <div className="edit-actions">
                                  <button
                                    className="save-edit-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSaveEdit(group.id);
                                    }}
                                    disabled={!editName.trim()}
                                  >
                                    Save
                                  </button>
                                  <button
                                    className="cancel-edit-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCancelEdit();
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="group-info">
                                  <div className="group-header">
                                    <h3>{group.name}</h3>
                                    {selectedGroupForShare?.id === group.id && (
                                      <span className="selected-badge">
                                        <Check size={14} />
                                        Selected
                                      </span>
                                    )}
                                    {defaultGroupId === group.id && (
                                      <span className="default-badge">Default</span>
                                    )}
                                    {isSharedWithGroup && (
                                      <span className="shared-badge">
                                        {itemsSharedWithGroup} shared
                                      </span>
                                    )}
                                  </div>
                                  <p className="group-meta">
                                    {group.type} • {group.member_count || 0} member{(group.member_count || 0) !== 1 ? 's' : ''} • {group.item_count || 0} item{(group.item_count || 0) !== 1 ? 's' : ''}
                                  </p>
                                  {group.description && (
                                    <p className="group-description">{group.description}</p>
                                  )}
                                </div>
                                
                                {selectedGroupForShare?.id === group.id && (
                                  <div className="group-actions">
                                    {/* Share/Unshare buttons */}
                                    {isSharedWithGroup ? (
                                      <button
                                        className="action-btn unshare"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleUnshareFromGroup(group.id);
                                        }}
                                        disabled={loading}
                                      >
                                        <X size={14} />
                                        Unshare
                                      </button>
                                    ) : (
                                      <button
                                        className="action-btn share"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleShareToGroup(group.id);
                                        }}
                                        disabled={loading}
                                      >
                                        <Check size={14} />
                                        Share
                                      </button>
                                    )}
                                    
                                    {group.role === 'owner' && (
                                      <>
                                        <button
                                          className="action-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedGroup(group);
                                            setShowMembersModal(true);
                                          }}
                                        >
                                          <Users size={14} />
                                          Members
                                        </button>
                                        <button
                                          className="action-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleStartEdit(group);
                                          }}
                                        >
                                          <Edit size={14} />
                                          Edit
                                        </button>
                                        <button
                                          className="action-btn delete"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteGroup(group.id);
                                          }}
                                        >
                                          <Trash2 size={14} />
                                          Delete
                                        </button>
                                      </>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                /* Create group form */
                <form onSubmit={handleCreateGroup} className="create-group-form">
                  <button
                    type="button"
                    className="back-button"
                    onClick={() => setGroupsView('select')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                    Back to groups
                  </button>
                  
                  <div className="form-group">
                    <label htmlFor="groupName">Group Name *</label>
                    <input
                      id="groupName"
                      type="text"
                      value={groupName}
                      onChange={(e) => setGroupName(e.target.value)}
                      placeholder="e.g., Smith Family, Office Team"
                      required
                      autoFocus
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="groupType">Group Type *</label>
                    <select
                      id="groupType"
                      value={groupType}
                      onChange={(e) => setGroupType(e.target.value as any)}
                    >
                      <option value="family">Family - Permanent household inventory</option>
                      <option value="team">Team - Permanent team/business inventory</option>
                      <option value="temporary">Temporary - Time-limited (e.g., moving)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="groupDescription">Description (Optional)</label>
                    <textarea
                      id="groupDescription"
                      value={groupDescription}
                      onChange={(e) => setGroupDescription(e.target.value)}
                      placeholder="What is this group for?"
                      rows={3}
                    />
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={setAsDefault}
                        onChange={(e) => setSetAsDefault(e.target.checked)}
                      />
                      <span>Add new items to this group</span>
                    </label>
                  </div>

                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading || !groupName.trim()}
                  >
                    {loading ? 'Creating...' : 'Create Group'}
                  </button>
                </form>
              )}
            </div>
          )}

          {activeTab === 'email' && !isBulkMode && (
            <div className="email-tab-content">
              {/* Sub-tabs for email */}
              {existingShares.length > 0 && (
                <div className="email-sub-tabs">
                  <button
                    className={`sub-tab ${emailView === 'share' ? 'active' : ''}`}
                    onClick={() => setEmailView('share')}
                  >
                    New Share
                  </button>
                  <button
                    className={`sub-tab ${emailView === 'manage' ? 'active' : ''}`}
                    onClick={() => setEmailView('manage')}
                  >
                    Manage ({existingShares.length})
                  </button>
                </div>
              )}

              {emailView === 'share' && !shareResult && (
                <form onSubmit={handleEmailShare} className="email-share-form">
                  <div className="form-group">
                    <label htmlFor="share-email">Recipient's Email</label>
                    <input
                      id="share-email"
                      type="text"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="friend@example.com, another@example.com"
                      required
                      autoFocus
                    />
                    <p className="form-hint">
                      Separate multiple emails with commas. If they're not on Wandering Sock yet, they'll get a link to view the item.
                    </p>
                  </div>

                  <div className="form-group">
                    <label>Permission</label>
                    <div className="permission-options">
                      <button
                        type="button"
                        className={`permission-option ${permission === 'view' ? 'active' : ''}`}
                        onClick={() => setPermission('view')}
                      >
                        <Eye size={16} />
                        <span>View only</span>
                      </button>
                      <button
                        type="button"
                        className={`permission-option ${permission === 'edit' ? 'active' : ''}`}
                        onClick={() => setPermission('edit')}
                      >
                        <Edit size={16} />
                        <span>Can edit</span>
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="share-message">Message (optional)</label>
                    <textarea
                      id="share-message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Add a note..."
                      rows={2}
                      onFocus={(e) => {
                        // Add padding to push content up when keyboard appears on iOS
                        const modalBody = document.querySelector('.manage-sharing-body');
                        if (modalBody) {
                          (modalBody as HTMLElement).style.paddingBottom = '300px';
                        }
                        setTimeout(() => {
                          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 100);
                      }}
                      onBlur={() => {
                        // Remove padding when keyboard closes
                        const modalBody = document.querySelector('.manage-sharing-body');
                        if (modalBody) {
                          (modalBody as HTMLElement).style.paddingBottom = '';
                        }
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="share-expires">
                      <Clock size={16} />
                      Expires (optional)
                    </label>
                    <select
                      id="share-expires"
                      value={expiresDays || ''}
                      onChange={(e) => setExpiresDays(e.target.value ? parseInt(e.target.value) : null)}
                    >
                      <option value="">Never</option>
                      <option value="1">1 day</option>
                      <option value="7">7 days</option>
                      <option value="30">30 days</option>
                      <option value="90">90 days</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading || !email.trim()}
                  >
                    {loading ? 'Sharing...' : 'Share'}
                  </button>
                </form>
              )}

              {emailView === 'share' && shareResult && (
                <div className="share-result">
                  <div className="share-result-icon">
                    <Check size={32} />
                  </div>
                  <h3>
                    {shareResult.recipient_is_user
                      ? `Shared with ${shareResult.recipient_name || shareResult.recipient_email}`
                      : 'Share link created!'}
                  </h3>
                  <p className="share-result-description">
                    {shareResult.recipient_is_user
                      ? `${shareResult.recipient_name || 'They'} can now see this item in their "Shared Items" view.`
                      : `Send this link to ${shareResult.recipient_email}. When they sign up, the item will appear in their inventory.`}
                  </p>
                  
                  <div className="share-link-box">
                    <input
                      type="text"
                      value={shareResult.share_url}
                      readOnly
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                    />
                    <button
                      className="copy-button"
                      onClick={handleCopyLink}
                      title="Copy link"
                    >
                      {copied ? <Check size={18} /> : <Copy size={18} />}
                    </button>
                    <a
                      href={shareResult.share_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="open-button"
                      title="Open link"
                    >
                      <ExternalLink size={18} />
                    </a>
                  </div>

                  <button
                    className="share-another-button"
                    onClick={handleNewEmailShare}
                  >
                    Share with someone else
                  </button>
                </div>
              )}

              {emailView === 'manage' && (
                <div className="existing-shares">
                  {existingShares.length === 0 ? (
                    <p className="no-shares">No active shares for this item.</p>
                  ) : (
                    <ul className="shares-list">
                      {existingShares.map((share) => (
                        <li key={share.id} className={`share-item ${!share.is_active ? 'revoked' : ''}`}>
                          <div className="share-info">
                            <div className="share-recipient">
                              <Mail size={14} />
                              <span>{share.recipient_email}</span>
                              {share.is_claimed && (
                                <span className="claimed-badge" title="Recipient has joined">
                                  <Users size={12} />
                                  Joined
                                </span>
                              )}
                            </div>
                            <div className="share-meta">
                              <span className={`permission-badge ${share.permission}`}>
                                {share.permission === 'view' ? <Eye size={12} /> : <Edit size={12} />}
                                {share.permission}
                              </span>
                              {share.viewed_at && (
                                <span className="viewed-badge" title={`Viewed: ${new Date(share.viewed_at).toLocaleString()}`}>
                                  Viewed
                                </span>
                              )}
                              {share.expires_at && (
                                <span className="expires-badge">
                                  <Clock size={12} />
                                  Expires {new Date(share.expires_at).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                          {share.is_active && (
                            <button
                              className="revoke-button"
                              onClick={() => handleRevokeShare(share.id)}
                              title="Revoke share"
                              disabled={loading}
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                          {!share.is_active && (
                            <span className="revoked-badge">Revoked</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Group Members Modal */}
        <GroupMembersModal
          isOpen={showMembersModal}
          onClose={() => {
            setShowMembersModal(false);
            setSelectedGroup(null);
          }}
          group={selectedGroup}
          onUpdate={loadGroups}
        />
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default ManageSharingModal;
